/**
 * Elementor Accordion Component
 *
 * Handles accordion expand/collapse behavior with ARIA support.
 * Supports both single-open (FAQ style) and multi-open (feature list style) modes.
 *
 * @package Wohngut
 * @since 1.0.0
 */

export default class ElementorAccordion {
	/**
	 * Constructor
	 *
	 * @param {HTMLElement} element - The accordion container element
	 */
	constructor(element) {
		this.accordion = element;
		this.behavior = element.dataset.behavior || 'single';
		this.items = [...element.querySelectorAll('[data-accordion-item]')];
		this.init();
	}

	/**
	 * Initialize accordion
	 */
	init() {
		// Store bound handlers for cleanup
		this.boundHandlers = new Map();

		this.items.forEach(item => {
			const header = item.querySelector('[data-accordion-header]');

			if (!header) {
				console.warn('Accordion item missing header element:', item);
				return;
			}

			// Create bound handlers for this item
			const clickHandler = () => this.handleClick(item);
			const keydownHandler = (e) => {
				if (e.key === ' ' || e.key === 'Enter') {
					e.preventDefault();
					this.handleClick(item);
				}
			};

			// Store handlers for cleanup
			this.boundHandlers.set(header, { click: clickHandler, keydown: keydownHandler });

			// Click handler
			header.addEventListener('click', clickHandler);

			// Keyboard support (Space and Enter keys)
			header.addEventListener('keydown', keydownHandler);
		});
	}

	/**
	 * Handle accordion item click
	 *
	 * @param {HTMLElement} clickedItem - The clicked accordion item
	 */
	handleClick(clickedItem) {
		if (this.behavior === 'single') {
			// Close all other items in single-open mode
			this.items.forEach(item => {
				if (item !== clickedItem && item.classList.contains('wohngut-accordion__item--open')) {
					this.close(item);
				}
			});
		}

		this.toggle(clickedItem);
	}

	/**
	 * Toggle accordion item state
	 *
	 * @param {HTMLElement} item - The accordion item to toggle
	 */
	toggle(item) {
		const isOpen = item.classList.contains('wohngut-accordion__item--open');
		const header = item.querySelector('[data-accordion-header]');

		// Toggle class
		item.classList.toggle('wohngut-accordion__item--open');

		// Update ARIA attribute
		if (header) {
			header.setAttribute('aria-expanded', !isOpen);
		}
	}

	/**
	 * Close accordion item
	 *
	 * @param {HTMLElement} item - The accordion item to close
	 */
	close(item) {
		const header = item.querySelector('[data-accordion-header]');
		item.classList.remove('wohngut-accordion__item--open');

		// Update ARIA attribute
		if (header) {
			header.setAttribute('aria-expanded', 'false');
		}
	}

	/**
	 * Destroy instance and cleanup event listeners
	 */
	destroy() {
		this.boundHandlers.forEach((handlers, header) => {
			header.removeEventListener('click', handlers.click);
			header.removeEventListener('keydown', handlers.keydown);
		});
		this.boundHandlers.clear();
	}
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-wohngut-accordion]').forEach(el => {
		new ElementorAccordion(el);
	});
});

// Elementor Frontend support (for editor preview and frontend rendering)
if (window.elementorFrontend && window.elementorFrontend.hooks) {
	window.elementorFrontend.hooks.addAction('frontend/element_ready/wohngut-accordion.default', ($scope) => {
		const element = $scope.find('[data-wohngut-accordion]')[0];
		if (element) {
			new ElementorAccordion(element);
		}
	});
}
